import axios from "axios";

let apiBaseUrl = null;
let publicUrlPrefix = '/uploads';
let publicAssetBaseUrl = '';
let clientUrlForApi = null;

const DEFAULT_MASTER_API_PATH = '/api/v1';
const DEFAULT_MASTER_HOST = 'http://localhost:3001';

const getWindow = () => (typeof window !== 'undefined' ? window : undefined);

const applyClientTimezoneHeaders = () => {
  try {
    const win = getWindow();
    if (!win) return;
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offsetMinutes = new Date().getTimezoneOffset();
    if (timeZone) {
      axios.defaults.headers.common['X-Client-Timezone'] = timeZone;
    }
    if (Number.isFinite(offsetMinutes)) {
      axios.defaults.headers.common['X-Client-Tz-Offset'] = String(offsetMinutes);
    }
  } catch (_) {
    // best-effort
  }
};

const resolveClientUrl = () => {
  const envUrl = process.env.REACT_APP_CLIENT_URL;
  if (envUrl && envUrl.trim()) {
    return envUrl.trim();
  }
  const win = getWindow();
  if (win && win.location && win.location.origin) {
    return win.location.origin;
  }
  return DEFAULT_MASTER_HOST;
};

const resolveMasterApiUrl = () => {
  const win = getWindow();
  const envUrl = process.env.REACT_APP_CLIENT_MASTER_API_URL;
  let baseCandidate;

  if (envUrl && envUrl.trim()) {
    baseCandidate = envUrl.trim();
  } else {
    const isLocalHost = (win && win.location && win.location.hostname === 'localhost');
    const fallbackBase = isLocalHost ? DEFAULT_MASTER_HOST : resolveClientUrl();
    baseCandidate = `${fallbackBase.replace(/\/$/, '')}${DEFAULT_MASTER_API_PATH}`;
  }

  try {
    const parsed = new URL(baseCandidate, DEFAULT_MASTER_HOST);
    if (win && win.location && win.location.protocol === 'https:' && parsed.protocol === 'http:') {
      parsed.protocol = 'https:';
    }
    return parsed.toString().replace(/\/$/, '');
  } catch (_) {
    return `${DEFAULT_MASTER_HOST}${DEFAULT_MASTER_API_PATH}`;
  }
};

const attachClientUrlInterceptor = () => {
  if (axios.__skcsClientUrlInterceptor) return;
  axios.interceptors.request.use((config) => {
    const clientUrl = clientUrlForApi || resolveClientUrl();
    if (!clientUrl) return config;
    const params = { ...(config.params || {}), ClientUrl: clientUrl };
    return { ...config, params };
  });
  axios.__skcsClientUrlInterceptor = true;
};

export async function loadApiConfig() {
  applyClientTimezoneHeaders();
  const hostDetails = { ClientUrl: resolveClientUrl() };
  clientUrlForApi = hostDetails.ClientUrl;
  attachClientUrlInterceptor();
  const masterApiUrl = resolveMasterApiUrl();

  try {
    const response = await axios.get(`${masterApiUrl}/getApiUri`, { params: hostDetails });
    if (response.status === 200) {
      const payload = response.data?.data?.[0];
      apiBaseUrl = payload?.APIUrl || null;
      if (!apiBaseUrl) {
        throw new Error('APIUrl missing in response payload');
      }
      try {
        const storageParams = { ...hostDetails };
        const appId = getApplicationIdFromEnv();
        if (appId !== null) {
          storageParams.ApplicationId = appId;
        }
        const storageRes = await axios.get(`${apiBaseUrl}/file/storage-config`, { params: storageParams });
        const storageData = storageRes?.data?.data;
        const prefix = storageData?.publicUrlPrefix;
        if (prefix) publicUrlPrefix = prefix;
        const assetBase = storageData?.publicAssetBaseUrl;
        if (assetBase) publicAssetBaseUrl = assetBase;
      } catch (_) {
        /* storage-config optional until table exists */
      }
      return payload;
    }
    throw new Error(`Unexpected response status ${response.status}`);
  } catch (err) {
    console.error('Failed to load API config', err);
    apiBaseUrl = `${DEFAULT_MASTER_HOST}${DEFAULT_MASTER_API_PATH}`;
    return null;
  }
}

export function getApiBaseUrl() {
  if (!apiBaseUrl) {
    console.warn('API base URL not loaded yet. Did you call loadApiConfig()?');
  }
  return apiBaseUrl;
}

export function ensureClientTimezoneHeaders() {
  applyClientTimezoneHeaders();
}

export function getPublicUrlPrefix() {
  return publicUrlPrefix || '/uploads';
}

export function getPublicAssetBaseUrl() {
  return publicAssetBaseUrl || '';
}

export function getClientUrl() {
  return clientUrlForApi || resolveClientUrl();
}

export function getApplicationIdFromEnv() {
  const raw = process.env.REACT_APP_APPLICATION_ID;
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return null;
  }
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/** Append ApplicationId to multipart uploads for storage path resolution (upload-only). */
export function appendApplicationId(formData) {
  const id = getApplicationIdFromEnv();
  if (id !== null && formData && typeof formData.append === 'function') {
    formData.append('ApplicationId', String(id));
  }
  return formData;
}

/** Query params for file API (axios POST delete, etc.). */
export function getFileApiQueryParams() {
  const params = {};
  const clientUrl = getClientUrl();
  if (clientUrl) {
    params.ClientUrl = clientUrl;
  }
  const appId = getApplicationIdFromEnv();
  if (appId !== null) {
    params.ApplicationId = appId;
  }
  return params;
}

/**
 * Build download/preview URL for browser img/video src (includes ClientUrl + ApplicationId).
 * @param {number|string} fileLibraryId
 * @param {{ inline?: boolean }} [options]
 */
export function buildFileDownloadUrl(fileLibraryId, options = {}) {
  const { inline = true } = options;
  const base = `${getApiBaseUrl()}/file/download/${fileLibraryId}`;
  const params = new URLSearchParams();
  const clientUrl = getClientUrl();
  if (clientUrl) {
    params.set('ClientUrl', clientUrl);
  }
  const appId = getApplicationIdFromEnv();
  if (appId !== null) {
    params.set('ApplicationId', String(appId));
  }
  if (inline) {
    params.set('inline', '1');
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Delete a filelibrary row and its physical file (body + query ApplicationId, like upload).
 * @param {number|string} fileLibraryId
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function deleteFileLibrary(fileLibraryId) {
  const body = { fileLibraryId };
  const appId = getApplicationIdFromEnv();
  if (appId !== null) {
    body.ApplicationId = appId;
  }
  return axios.post(getApiBaseUrl() + '/file/delete', body, {
    params: getFileApiQueryParams()
  });
}
